import React, { useState } from "react";

import { useGlobalHandler } from "../core/hooks/useGlobalHandler";
import { closest, getElementKey } from "../core/utils/dom";
import { useConditional } from "../core/hooks/useConditional";
import { Modal } from "../core/ui/Modal";
import { DataContext, useDataContext } from "../core/hooks/useData";
import { Text } from "../core/form/Text";
import { broadcast, useChannel } from "../core/hooks/useChannel";
import { CalculatedElement, ContainerElement, ContainerState, Element, EventType, FieldMessages, RefTableElement } from "./types";
import { newID } from "../core/utils/unique";
import { Checkbox } from "../core/form/Checkbox";
import { api } from "../api/api";
import { AllEntityTypes, GroupLayoutOptions, ScriptLanguages, getEntityTypeName, ref } from "../types";
import { Dropdown } from "../core/form/Dropdown";
import { Table } from "../core/ui/Table";
import { JSEditor } from "../core/ui/JSEditor";
import { Icon } from "../core/ui/icons";
import { EditableRegion } from "../core/form/EditableRegion";
import { FormFeatureTypeCol } from "../layout/TableCells";
import { isString } from "rant-utils";

export const InspectorMessages = {
	EDIT: "edit",

	ADD_ABOVE: "add-above",
	ADD_BELOW: "add-below",
	MOVE_UP: "move-up",
	MOVE_DOWN: "move-down",
	DELETE: "delete",
}

export const inspectorChannel = "inspector";

const controlTypes = [
	{ id: "text", name: "Text" },
	{ id: "checkbox", name: "Checkbox" },
	{ id: "radio", name: "Radio Buttons" },
	{ id: "list", name: "List" },
	{ id: "dropdown", name: "Dropdown" },
	{ id: "container", name: "Container" },
	{ id: "sub-form", name: "Sub Form" },
	{ id: "heading", name: "Heading" },
	{ id: "aus-address-search", name: "Australian Address Search" },
	{ id: "info", name: "Information" },
	{ id: "resource-picker", name: "Resource Picker" },
	{ id: "date", name: "Date" },
	{ id: "acn", name: "ACN" },
	{ id: "legal-lement", name: "Legal Element" },
	{ id: "calc", name: "Calc" },
	{ id: "button", name: "Button" },
	{ id: "entity-lookup", name: "Entity Lookup" },
	{ id: "report", name: "Report" },
	{ id: "succession-plan", name: "Succession Plan" },
	{ id: "group", name: "Group" },
	{ id: "checkbox-list", name: "Checkbox List" },
	{ id: "icon-choice", name: "Icon Choice" },
	{ id: "dynamic-dropdown", name: "Dynamic Dropdown" },
	{ id: "chart", name: "Chart" },
	{ id: "gateway-form", name: "Gateway Form" },
	{ id: "script", name: "Script" },
];

export function InspectorEditModal(props: {

}) {

	const inspector = useInspector();

	const [, refresh] = React.useState({});

	const data = useDataContext<Element>({
		initialValue: {} as Element,
	});

	const modal = useConditional((modalProps: {
		def: Element,
		fieldID: string,
	}) => {

		const { def } = modalProps;

		let specific;

		if (def.type === "text") {
			specific = <>
				<Checkbox prop="multiline" title="Multiline" />
				<Checkbox prop="wide" title="Wide" />
				<Text prop="size" title="Size" />
				<Text prop="typeIndicator" title="Type indicator" />
				<Text prop="quickChoices" title="Quick select choices (comma separated)" />
			</>;

		} else if (def.type === "heading") {
			specific = <>
				<Checkbox prop="outline" title="Include in outline" />
			</>;

		} else if (
			(def.type === "radio") ||
			(def.type === "dropdown") || 
			(def.type === "list") ||
			(def.type === "icon-choice") 
		) {
			const e = def as RefTableElement;
			const refTable = inspector.refTables.find((v) => (v.id === e.ref));

			specific = <>
				{/* <Text prop="ref" title="Ref Table" /> */}
				<Dropdown
					prop="ref"
					title="Ref Table"
					items={inspector.refTables}
					getDisplay={(item) => item.title}
					getValue={(item) => item.id}
					onChange={() => {
						refresh({});
					}}
				/>

				<Dropdown
					prop="valueCol"
					title="Value Column"
					items={refTable ? refTable.data.columns : []}
					getDisplay={(item) => item.title}
					getValue={(item) => item.id}
				/>
				<Dropdown
					prop="textCol"
					title="Text Column"
					items={refTable ? refTable.data.columns : []}
					getDisplay={(item) => item.title}
					getValue={(item) => item.id}
				/>
				<Dropdown
					prop="subtitleCol"
					title="Subtitle Column"
					items={refTable ? refTable.data.columns : []}
					getDisplay={(item) => item.title}
					getValue={(item) => item.id}
				/>
				{
					def.type === "icon-choice" &&
					<Dropdown
						prop="iconCol"
						title="Icon Column"
						blankOption={{ value: "" }}
						items={refTable ? refTable.data.columns : []}
						getDisplay={(item) => item.title}
						getValue={(item) => item.id}
					/>
				}
				<Dropdown
					prop="filterCol"
					title="Filter Column"
					blankOption={{ value: "" }}
					items={refTable ? refTable.data.columns : []}
					getDisplay={(item) => item.title}
					getValue={(item) => item.id}
				/>
				<div>
					Filter Script
					<JSEditor object={def} prop="filterScript" />
				</div>
				<Dropdown
					prop="sortCol"
					title="Sort Column"
					blankOption={{ value: "" }}
					items={refTable ? refTable.data.columns : []}
					getDisplay={(item) => item.title}
					getValue={(item) => item.id}
				/>

				<Checkbox prop="includeBlank" title="Include blank" />
				{(def.type === "radio") && <Text prop="layout" title="Layout" />}
				{(def.type === "dropdown" || def.type === "list") && <Checkbox prop="multiSelect" title="Multiselect" />}

			</>;

		} else if (def.type === "container") {
			specific = <>
				<Text prop="repeat.itemName" title="Repeating Item Name" />
				<Text prop="repeat.overviewTitleProp" title="Overview Title Prop" />
				<Text prop="summaryReport" title="Summary Report Form Element" />
			</>;

		} else if (def.type === "sub-form") {
			specific = <>
				<Text prop="form" title="Form" />
				<Checkbox prop="bg" title="Has Background" />

				<fieldset>
					<legend>Options</legend>
					<JSEditor object={def} prop="options" />
				</fieldset>
			</>;

		} else if (def.type === "report") {
			specific = <>
				<Text prop="report" title="Report" />
				<Checkbox prop="isStatic" title="Static" />

				<fieldset>
					<legend>Options</legend>
					<JSEditor object={def} prop="options" />
				</fieldset>
			</>;

		} else if (def.type === "calc") {
			specific = <>
				<Checkbox prop="calcOnSaveOnly" title="Calculate on Save only" /><br/>
				<JSEditor prop="script" />
			</>;

		} else if (def.type === "info") {
			specific = <>
				<Text prop="className" title="CSS class" />
				<Text prop="html" isMultiline={true} style={{width:"100%"}} />
			</>;

		} else if (def.type === "entity-lookup") {
			specific = <>
				<Dropdown
					prop="requiredTypes"
					title="Required types"
					items={AllEntityTypes}
					getDisplay={(item: string) => getEntityTypeName(item)}
					getValue={(item: string) => item}
					multiSelect={true}
				/>
				<fieldset>
					<legend>Field names</legend>
					<Text prop="companyProp" title="Company name" />
					<Text prop="acnProp" title="Company ACN" />
					<Text prop="addressProp" title="Address" />
				</fieldset>
			</>;

		} else if (def.type === "succession-plan") {

		} else if (def.type === "group") {
			specific = <>
				<Dropdown
					prop="layout"
					title="Layout"
					items={GroupLayoutOptions}
					getDisplay={(item) => item.name}
					getValue={(item) => item.id}
				/>
			</>;

		} else if (def.type === "checkbox-list") {
			specific = <>
				<JSEditor prop="getOptionsScript" />
			</>;

		} else if (def.type === "dynamic-dropdown") {
			specific = <>
				<Checkbox prop="multiSelect" title="Multiselect" />
				<Checkbox prop="oneSelectionAtTime" title="One choice at a time" />
				<fieldset>
					<legend>Get Options Script</legend>
					<JSEditor prop="getOptionsScript" />
				</fieldset>
			</>;

		} else if (def.type === "button") {
			specific = <>
				<JSEditor prop="onClick" />
			</>;

		} else if (def.type === "script") {
			specific = <>
				<Dropdown
					prop="lang"
					title="Language"
					items={ScriptLanguages}
					getDisplay={(item) => item.name}
					getValue={(item) => item.id}
				/>
			</>;

		}


		if (!def.validations) def.validations = [];
		if (!def.events) def.events = {};

		return (
			<DataContext.Provider value={data}>
				<Modal
					title={`Edit - ${data.value.type}`}

					size="width-75vw"

					showCancel={false}

					onSave={() => {
						if (def.type === "container") {
							const c = def as ContainerElement;
							if (c.repeat && !c.repeat.itemName) {
								delete c.repeat;
							}
						}

						// refresh the parent container
						broadcast(modalProps.fieldID, {
							type: FieldMessages.REFRESH_PARENT_CONTAINER,
						})
						modal.close();						
					}}
					saveOnEnterKey={false}

				>
					<div className="flex horiz align-start gap-5 children-even-2">
						<fieldset className="width-50pc">
							<legend>Common</legend>
							{/* <Text prop="type" title="Control type" /> */}
							<Dropdown
								prop="type"
								title="Control type"
								items={controlTypes}
								getDisplay={(item) => item.name}
								getValue={(item) => item.id}
								onChange={() => {
									refresh({});
								}}
							/>

							<Text prop="id" title="ID" />
							<Text prop="title" title="Title" />
							<Text prop="subTitle" title="Subtitle" />
							<Text prop="titleType" title="Title type" />
							<Checkbox prop="required" title="Required?" />
							<Text prop="defaultValue" title="Default value" />
							<JSEditor object={def} prop="defaultValueScript" />
							<Text prop="comments" title="Comments" />
						</fieldset>

						<div className="flex vert gap-5 width-50pc">
							<fieldset className="width-100pc">
								<legend>{def.type}</legend>
								{specific}
							</fieldset>
							<fieldset className="width-100pc">
								<legend>Features</legend>
								<Features def={def} />
							</fieldset>
						</div>
					</div>

					
					<div className="flex horiz align-start gap-5">
						<fieldset className="width-50pc">
							<legend>Validations</legend>
							<Validations def={def} />
						</fieldset>
						<fieldset className="width-50pc">
							<legend>Events</legend>
							<Events def={def} />
						</fieldset>
					</div>
					
					

				</Modal>

			</DataContext.Provider>
		);
	});

	useChannel(inspectorChannel, React.useCallback((msg) => {
		if (msg.type === InspectorMessages.EDIT) {
			const def = msg.def;
			const fieldID = msg.fieldID;
			modal.show({
				fieldID,
				def
			});
			data.value = def;
		}

	}, [modal, data]));


	return (
		modal.render()
	)
}

export function EditFeatureModal(props: {
	val: any,
	onDelete: () => void,
	onSave: () => void,
}) {

	const val = props.val;
	const data = useDataContext({ initialValue: val });

	const inspector = useInspector();

	const [,refresh] = React.useState({});

	if (!val.options || !isString(val.options)) val.options = "";

	return (
		<DataContext.Provider value={data}>
			<Modal
				title="Edit Feature"
				saveTitle="Close"
				size="width-50vw"
				onSave={props.onSave}
				onCancel={props.onSave}
				showDelete={true}
				onDelete={() => {
					props.onDelete();
				}}

				saveOnEnterKey={false}

			>
				<fieldset>

					<JSEditor prop="options" object={val} />

				</fieldset>
			</Modal>
		</DataContext.Provider>
	);
}

export function Features(props: {
	def: Element,
}) {

	const [,refresh] = useState({});

	const editModal = useConditional((val: any) => {

		return (
			<EditFeatureModal
				val={val}
				onSave={editModal.close}
				onDelete={() => {
					const i = props.def.features!.indexOf(val);
					props.def.features?.splice(i, 1);
					editModal.close();
				}}
			/>
		);
	});

	return (
		<>
			<Table
				cols={[
					FormFeatureTypeCol(),
				]}
				items={props.def.features}
				onClick={(e, r) => {
					editModal.show(r);
				}}
			/>
			<button
				onClick={() => {
					if (!props.def.features) props.def.features = [];
					props.def.features.push({
						type: "entity-search",
					});
					refresh({});
				}}
			>Add Entity Search</button>
			{editModal.render()}
		</>
	)
}

export function EditActionModal(props: {
	action: any,
	onSave: () => void,
	onDelete: () => void,
}) {

	const data = useDataContext({ initialValue: props.action });

	const inspector = useInspector();

	const refTypes = inspector.refTables.find((item) => item.id === "form-action-types");

	const [,refresh] = React.useState({});

	function save() {
		// tidy empty props
		const options = props.action.options
		for(let m in options) {
			if (options[m] === "") delete options[m];
		}
		props.onSave();
	}

	return (
		<DataContext.Provider value={data}>
			<Modal
				title="Edit Action"
				size="width-75vw"

				onCancel={save}
				onSave={save}
				saveTitle="Close"
				
				showDelete={true}
				onDelete={props.onDelete}

				saveOnEnterKey={false}
			>
				<div className="flex horiz gap-5 align-start">
					<fieldset>
						<Dropdown
							prop="type"
							title="Type"
							items={refTypes!.data.rows}
							getValue={(i) => i[refTypes?.data.columns[0].id]}
							getDisplay={(i) => i[refTypes?.data.columns[1].id]}
							onChange={() => {
								refresh({});
							}}
						/>
					</fieldset>
					<fieldset>
						{
							props.action.type === "script"
							&& <JSEditor prop="options.script" />
						}
						{
							props.action.type === "visibility"
							&& <JSEditor prop="options.criteria" />
						}
					</fieldset>
				</div>

			</Modal>
		</DataContext.Provider>
	);
}

export function EditEventModal(props: {
	type: EventType,
	actions: any[],
	onSave: () => void,
}) {
	const { actions } = props;

	const editActionModal = useConditional((a: any) => {
		return (
			<EditActionModal
				action={a} 
				onSave={editActionModal.close}
				onDelete={() => {
					const i = actions.indexOf(a);
					actions.splice(i, 1);
					editActionModal.close();
				}}
			/>
		);
	});
	
	return (
		<Modal
			title={"Edit Actions - " + props.type}
			saveTitle="Close"
			size="width-50vw"
			onSave={props.onSave}
			onCancel={props.onSave}

			saveOnEnterKey={false}
		>

			<button onClick={() => {
				const a = {
					type: "script",
					options: {
					},
				};
				actions.push(a);
				editActionModal.show(a);
			}}>Add Action</button>

			<Table
				items={actions}
				cols={[
					{ title: "Type" },
					{ title: "Options" },
				]}
				interactive={true}
				render={{
					colTitle: (c) => c.title,
					colValue: (r, c) => {
						if (c.title === "Options") {
							return JSON.stringify(r.options);
						} else {
							return r.type;
						}
					}
				}}
				onClick={(e, r) => {
					editActionModal.show(r);
				}}
			/>

			{editActionModal.render()}

			{/* <fieldset>
				{/* <Dropdown
					prop="type"
					title="Type"
					items={refActionTypes!.data.rows}
					getValue={(i) => i[refActionTypes?.data.columns[0].id]}
					getDisplay={(i) => i[refActionTypes?.data.columns[1].id]}
					onChange={() => {
						refresh({});
					}}
				/> */}

				{/* {
					evt.type === "script" && 
					<JSEditor prop={evt.options.script} />
				} */}

			{/* </fieldset> */}
		</Modal>
	);
}


export function Events(props: {
	def: Element,
}) {

	const { def } = props;

	const editModal = useConditional((data: { type: EventType, actions: any[] }) => {

		return (
			<EditEventModal
				{...data}
				onSave={editModal.close}
			/>
		);
	});

	return (
		<>
			<button
				onClick={() => {
					if (!def.events!.change) def.events!.change = [];
					editModal.show({
						type: "change",
						actions: def.events!.change,
					});
				}}
			>On Change</button>
			<button
				onClick={() => {
					if (!def.events!["before-save"]) def.events!["before-save"] = [];
					editModal.show({
						type: "before-save",
						actions: def.events!["before-save"],
					});
				}}
			>On Before Save</button>
			<button
				onClick={() => {
					if (!def.events!["delete-item"]) def.events!["delete-item"] = [];
					editModal.show({
						type: "delete-item",
						actions: def.events!["delete-item"],
					});
				}}
			>On Delete Item</button>

			{editModal.render()}
		</>
	);
}



export function EditValidationModal(props: {
	val: any,
	onDelete: () => void,
	onSave: () => void,
}) {

	const val = props.val;
	const data = useDataContext({ initialValue: val });

	const inspector = useInspector();

	const refTypes = inspector.refTables.find((item) => item.id === "validation-types");
	const refDataTypes = inspector.refTables.find((item) => item.id === "data-types");

	const [,refresh] = React.useState({});

	if (!val.options) val.options = {};

	return (
		<DataContext.Provider value={data}>
			<Modal
				title="Edit Validation"
				saveTitle="Close"
				size="width-50vw"
				onSave={props.onSave}
				onCancel={props.onSave}
				showDelete={true}
				onDelete={() => {
					props.onDelete();
				}}

				saveOnEnterKey={false}

			>
				<fieldset>
					<Dropdown
						prop="type"
						title="Type"
						items={refTypes!.data.rows}
						getValue={(i) => i[refTypes?.data.columns[0].id]}
						getDisplay={(i) => i[refTypes?.data.columns[1].id]}
						onChange={() => {
							refresh({});
						}}
					/>

					{
						((val.type === "range") || (val.type === "char-limit")) &&
						<>
							<Text prop="options.min" title="Min" />
							<Text prop="options.max" title="Max" />
						</>
					}

					{
						(val.type === "age") &&
						<>
							<Text prop="options.minYears" title="Min Years" />
							<Text prop="options.maxYears" title="Max Years" />
						</>
					}

					{
						(val.type === "data-type") &&
						<Dropdown
							prop="options.type"
							title="Data Type"
							items={refDataTypes!.data.rows}
							getValue={(i) => i[refDataTypes?.data.columns[0].id]}
							getDisplay={(i) => i[refDataTypes?.data.columns[1].id]}
							onChange={() => {
								refresh({});
							}}
						/>

					}

					{
						(val.type === "occurrence-range") &&
						<>
							<Text prop="options.list" title="List" />
							<Text prop="options.prop" title="Prop to check" />
							<Text prop="options.min" title="Min" />
							<Text prop="options.minMessage" title="Min Message" />
							<Text prop="options.max" title="Max" />
							<Text prop="options.maxMessage" title="Max Message" />
						</>
					}

{
						(val.type === "custom") &&
						<>
							<JSEditor prop="script" object={val.options} />
							<Text prop="options.message" title="Error Message" />
						</>
					}

				</fieldset>
			</Modal>
		</DataContext.Provider>
	);
}

export function Validations(props: {
	def: Element,
}) {

	const { def } = props;

	const [, refresh] = React.useState({});

	const editModal = useConditional((val: any) => {

		return (
			<EditValidationModal
				val={val}
				onSave={editModal.close}
				onDelete={() => {
					const i = def.validations!.indexOf(val);
					def.validations?.splice(i, 1);
					editModal.close();
				}}
			/>
		);
	});

	return (
		<>
			<button
				onClick={() => {
					def.validations?.push({
						type: "required",
					});
					refresh({});
				}}
			>Add Validation</button>
			<Table
				items={def.validations}
				cols={[
					{ title: "Type" },
					{ title: "Options" }
				]}
				render={{
					colTitle: (c) => c.title,
					colValue: (r, c) => {
						if (c.title === "Options") {
							return JSON.stringify(r.options);
						} else {
							return r.type;
						}
					},
				}}
				interactive={true}
				onClick={(e, r) => {
					editModal.show(r);
				}}
			/>
			{editModal.render()}
		</>
	);
}


export interface InspectorState {
	refTables: ref[],
}

export const InspectorContext = React.createContext<InspectorState>({} as InspectorState);

export function useInspector() {
	return React.useContext(InspectorContext);
}

export function InspectorContainer(props: {
	children?: any,
}) {
	
	const context = React.useRef({
		refTables: [] as ref[],		
	});

	const [, refresh] = React.useState({});

	React.useEffect(() => {
		api.ref_get({}).then((result) => {
			context.current.refTables = result as ref[];
			refresh({});
		}).catch(() => {

		})
	}, []);

	// NOTE: don't render until ready otherwise Presenter gets in semi-connected state
	if (context.current.refTables.length <= 0) return null;
	return (
		<InspectorContext.Provider value={context.current}>
			{props.children}
		</InspectorContext.Provider>
	);
}


interface FocusedInfo {
	el: any,
	def: Element,
	fieldID: string,
	container: ContainerState,
}

export function Inspector(props: {

}) {

	const focused = React.useRef<FocusedInfo>();
	const toolbar = React.useRef<any>();

	const [, refresh] = React.useState({});

	function clearFocus() {
		if (!focused.current) return;

		const el = focused.current.el;
		el.style.background = "";

		focused.current = undefined;

		toolbar.current.style.visibility = "hidden";

		refresh({});
	}

	function setFocus(el: any, e: any) {
		if (focused.current && focused.current.el === el) return;

		if (focused.current) {
			// is the new item to focus too close
			const rExisting = focused.current.el.getBoundingClientRect();
			const dx = Math.abs(rExisting.left - e.screenX);
			if (dx < 50) return;
		}

		if (focused.current && focused.current.el) focused.current.el.style.background = "none";

		const fieldID = getElementKey(el);
		if (!fieldID) return;
		const msg: any = {
			type: FieldMessages.GET_CONTAINER,
		};
		broadcast(fieldID, msg);

		focused.current = {
			el,
			def: msg.def,
			fieldID,
			container: msg.container as ContainerState,
		};

		if (toolbar.current) toolbar.current.style.visibility = "visible";

		refresh({});

	}

	React.useLayoutEffect(() => {
		if (!focused.current) return;

		const { el } = focused.current;

		el.style.background = `
		repeating-linear-gradient(
			-45deg,
			transparent,
			transparent 1px,
			#ccc 3px,
			#ccc 1px
			)		
		`;

		toolbar.current.style.visibility = "visible";

	}, [focused.current]); // NOTE: despite the annoying compile warning, this is actually required

	useGlobalHandler("mousemove", (e: any) => {

		// ignore any movement over toolbar
		if (closest(e.target, ".inspector")) return;

		const field = closest(e.target, ".field");

		if (field) {
			setFocus(field, e);
		}
	
	});

	// NOTE: as the container re-renders new ids can be generated for the focused item
	// this is because the ids are used at runtime and generated automatically
	// so have to check if its changed and if so refresh the focused attribs to match actual elements on screen
	// this way the inspector can stay open for a given field despite it being re-rendered behind its back
	function recheckFocus() {
		if (!focused.current) return;

		const f = focused.current.container.getField(focused.current.def.id);
		if (f && f.channelID !== focused.current.fieldID) {
			focused.current.el = document.getElementById(f.channelID);
			focused.current.fieldID = f.channelID;
		}
	}

	function addRelative(offset: number) {
		if (!focused.current) return;
		recheckFocus();

		const msg: any = {
			type: FieldMessages.GET_CONTAINER,
		};
		broadcast(focused.current.fieldID, msg);

		const container = msg.container as ContainerState;

		const f = msg.def as Element;
		const thisIndex = container.def.elements.indexOf(f);

		const elementID = newID();
		const newElement = {
			type: "text",
			id: elementID,
			title: elementID,
		};

		container.def.elements.splice(thisIndex + offset, 0, newElement as any);

		msg.container.refresh();
	}

	function deleteFocused() {
		if (!focused.current) return;
		recheckFocus();

		const msg: any = {
			type: FieldMessages.GET_CONTAINER,
		};
		broadcast(focused.current.fieldID, msg);

		const container = msg.container as ContainerState;
		const f = msg.def as Element;
		const thisIndex = container.def.elements.indexOf(f);
		container.def.elements.splice(thisIndex, 1);
		msg.container.refresh();
		clearFocus();
	}

	function sort(offset: number) {
		if (!focused.current) return;
		recheckFocus();

		const msg: any = {
			type: FieldMessages.GET_CONTAINER,
		};
		broadcast(focused.current.fieldID, msg);
		const container = msg.container as ContainerState;
		const f = msg.def as Element;
		const thisIndex = container.def.elements.indexOf(f);

		container.def.elements.splice(thisIndex, 1);
		container.def.elements.splice(thisIndex+offset, 0, f);

		msg.container.refresh();
 	}

	 useChannel(inspectorChannel, React.useCallback((msg) => {
		if (msg.type === InspectorMessages.ADD_ABOVE) {
			addRelative(0);
		} else if (msg.type === InspectorMessages.ADD_BELOW) {
			addRelative(1);
		} else if (msg.type === InspectorMessages.MOVE_UP) {
			sort(-1);
		} else if (msg.type === InspectorMessages.MOVE_DOWN) {
			sort(1);
		} else if (msg.type === InspectorMessages.DELETE) {
			deleteFocused();
		}

	}, []));


	if (!focused.current) return null;

	const def = focused.current!.def!;



	// NOTE: for quick and easy editing of visibility which is commonly used, ensure objs in place for this to be picked up by code below
	if (!def.events) def.events = {};
	if (!def.events.change) {
		def.events.change = [{ type: "visibility", options: {} }];
	} else {
		let found = false;
		for(let a of def.events.change) {
			if (a.type === "visibility") {
				found = true; break;
			}
		}
		if (!found) def.events.change.push({ type: "visibility", options: {} });
	}


	let events = [];
	if (def.events && def.events.change) {
		const actions = def.events.change;

		let i = 0;
		for(let a of actions) {
			if (a.type === "script") {
				events.push(
					<div className="tag subtle" key="scr">On Change</div>,
					<JSEditor object={a.options} prop="script" key={i} />
				)					
			} else if (a.type === "visibility") {
				events.push(
					<div className="tag subtle" key="vis">Visibility Criteria</div>,
					<JSEditor object={a.options} prop="criteria" key={i} />
				)					


			}

			i++;
		}
	}

	const rect = focused.current.el.getBoundingClientRect();

	// let calcScript;
	// if (def.type === "calc") {
	// 	const calc = def as CalculatedElement;
	// 	if (calc.script) {
	// 		calcScript = <JSEditor object={calc} prop="script" key={def.id + "-script"} />;
	// 	}
	// }


	return (
		<div
			className="inspector fixed z-modal flex vert"
			ref={toolbar}
			style={{
				visibility:"hidden", 
				//left:(rect.left + (rect.width/2)) + "px", 
				top:(rect.top)+"px",
				right: "0",
			}}
		>

			{/* <button
				className="secondary small fixed z-modal overlay-button at-top"
				style={{left:(rect.left - 20) + "px", top:(rect.top - 20)+"px"}}
				onClick={() => {
					addRelative(0);
				}}
			><Icon type="add" size="small" /></button> */}
			{/* <button
				className="secondary small fixed z-modal overlay-button at-top"
				style={{left:(rect.left+5) + "px", top:(rect.top - 20)+"px"}}
				onClick={() => {
					sort(-1);
				}}
			><Icon type="arrowUp" size="small" /></button> */}

			<button
				className="secondary small fixed z-modal overlay-button large"
				style={{left:(rect.left - 30) + "px", top:(rect.top + rect.height/2 - 15)+"px"}}
				onClick={() => {
					if (focused.current) {

						const fieldID = focused.current.fieldID;

						const msg: any = {
							type: FieldMessages.GET_DEF,
						};
						broadcast(fieldID, msg);

						broadcast(inspectorChannel, {
							type: InspectorMessages.EDIT,
							def: msg.def,
							fieldID,
						});
					}
				}}
			><Icon type="edit" /></button>
			

			{/* <button
				className="secondary small fixed z-modal overlay-button at-bottom"
				style={{left:(rect.left - 20) + "px", top:(rect.top + rect.height)+"px"}}
				onClick={() => {
					addRelative(1);
				}}
			><Icon type="add" size="small" /></button> */}
			{/* <button
				className="secondary small fixed z-modal overlay-button at-bottom"
				style={{left:(rect.left + 5) + "px", top:(rect.top + rect.height)+"px"}}
				onClick={() => {
					sort(1);
				}}
			><Icon type="arrowDown" size="small" /></button> */}

			<button
				className="secondary small fixed z-modal overlay-button delete"
				style={{left:(rect.left - 50) + "px", top:(rect.top + (rect.height/2) - 10)+"px"}}
				onClick={() => {
					deleteFocused();
				}}
			><Icon type="cross" size="small" /></button>


			<div className="tag secondary width-100pc flex vert align-center" style={{display:"flex", right:0}}>
				<EditableRegion
					intialHtml={def.id} 
					onChange={(e) => {
						def.id = e.target.value;
					}}
				/>
				<span>{focused.current?.def.type}</span>
			</div>

			<div className="flex horiz align-start">
				{/* {calcScript} */}

				{
					(events.length > 0) &&
					<div>
						{events}
					</div>
				}

			</div>



			<InspectorEditModal />

		</div>
	);

}
